import classNames from 'classnames'
import {gsap} from 'gsap'
import React, {useEffect, useRef} from 'react'

import {makeEditable} from '@/common/utils'
import Buttons from '@/components/Buttons'
import {Grid} from '@/components/Grid'
import Heading from '@/components/Heading'
import Image from '@/components/Image'
import Images from '@/components/Images'
import Testimonial from '@/components/Testimonial'

const CallToAction = ({
  backgroundColors,
  backgroundColor = 'yellow-400',
  headingTag,
  heading,
  buttons,
  images = [],
  fullWidth,
  testimonial,
  _editable,
}) => {
  const HeadingTag = headingTag || 'p'
  const sectionRef = useRef()

  useEffect(() => {
    if (sectionRef.current) {
      const context = gsap.context(() => {
        const imagesContainer = gsap.utils.toArray(document.getElementsByClassName('cta-image '))
        const images = gsap.utils.toArray('img')
        imagesContainer.forEach((image, index) => {
          const yPercent = gsap.utils.wrap([10, -25, 40], index)
          if (yPercent) {
            gsap.to(image, {
              yPercent,
              ease: 'linear',
              scrollTrigger: {
                trigger: sectionRef.current,
                start: 'top 80%',
                end: 'bottom 20%',
                scrub: 2,
              },
            })
          }
        })

        gsap.to(images, {
          scaleX: 1,
          scaleY: 1,
          scrollTrigger: {
            trigger: images,
            start: 'top 80%',
            scrub: 1,
          },
        })
      }, sectionRef.current)

      return () => context.revert()
    }
  }, [])

  return (
    <section
      ref={sectionRef}
      {...makeEditable(_editable)}
      className={classNames(backgroundColors.previous, {
        'relative left-1/2 right-1/2 my-[theme(spacing.26)] -ml-[50vw] -mr-[50vw] w-screen rounded-t-[theme(spacing.20)] lg:mb-2':
          fullWidth,
      })}
    >
      <div
        className={classNames(
          'relative overflow-hidden rounded-t-[80px]',
          {'bg-yellow-400': backgroundColor === 'yellow-400'},
          {'bg-white': backgroundColor === 'white'},
          {'py-70 lg:py-78': !testimonial && images.length > 0},
          {'py-18 md:py-30 lg:py-46 lg:pb-40': testimonial || images.length <= 0}
        )}
      >
        <Grid>
          {!!testimonial && (
            <div className='relative col-span-full mb-8 pt-60 md:mb-18 md:px-6 md:pt-12 lg:col-span-8 lg:col-start-3 lg:mb-22'>
              <Images
                images={images}
                className='absolute top-0 left-1/2 mx-auto flex h-62 w-[90%] -translate-x-1/2 justify-center md:left-6 md:h-[80%] md:w-[60%] md:translate-x-0'
                imageProps={{
                  sizes: {sm: 80, md: 50},
                  className: 'absolute inset-0 h-full w-full object-cover rounded-3xl',
                }}
              />
              <Testimonial
                {...testimonial.content}
                theme='blue'
                className='relative md:ml-[40%] md:w-[60%]'
              />
            </div>
          )}

          <div className='col-span-full col-start-1 md:col-span-6 lg:col-span-10 lg:col-start-2'>
            <HeadingTag className='text-center'>
              <Heading heading={heading} />
            </HeadingTag>
          </div>

          {!!buttons.length && (
            <div className='col-span-full flex justify-center'>
              <Buttons buttons={buttons} className='mt-12 flex gap-3' />
            </div>
          )}

          {!testimonial &&
            images.map((image, i) => {
              return (
                <div
                  key={i}
                  className={classNames(
                    'cta-image absolute w-29 overflow-hidden rounded-2xl lg:w-50',
                    i === 0
                      ? 'bottom-25 left-0 -translate-x-4 lg:top-1/2'
                      : i === 1
                      ? 'bottom-25 right-0 hidden md:block lg:top-1/2 lg:right-20'
                      : i === 2
                      ? 'bottom-0 right-0 translate-x-4 md:left-1/3 md:translate-x-0 md:translate-y-5 lg:translate-y-10'
                      : i === 3
                      ? 'top-0 left-1/4 -translate-y-5 lg:-translate-y-15'
                      : 'top-0 right-1/4 hidden md:block md:-translate-y-10 lg:-translate-y-30'
                  )}
                >
                  <Image
                    {...image}
                    className='h-full w-full scale-125 object-cover'
                    sizes={{sm: 29 * 4, lg: 50 * 4, unit: 'px'}}
                    ratio='9:12'
                    fullWidth
                  />
                </div>
              )
            })}
        </Grid>
      </div>
    </section>
  )
}

export default CallToAction
